"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { SearchIcon } from "lucide-react"
import { debounce } from "radash"

import { Category } from "@/lib/types"
import { Input } from "@/components/ui/input"

interface MenuSearchProps {
  categories: Category[]
  onSearchResults: (filteredCategories: Category[]) => void
}

export function MenuSearch({ categories, onSearchResults }: MenuSearchProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const workerRef = useRef<Worker | null>(null)

  // Initialize web worker
  useEffect(() => {
    if (typeof window !== "undefined") {
      workerRef.current = new Worker(
        new URL("@/lib/workers/search-dishes.ts", import.meta.url)
      )

      workerRef.current.onmessage = (e) => {
        onSearchResults(e.data.filteredCategories)
      }

      return () => {
        workerRef.current?.terminate()
      }
    }
  }, [onSearchResults])

  // Debounced search function
  const handleSearch = useCallback(
    debounce(
      {
        delay: 300,
      },
      (value: string) => {
        if (workerRef.current) {
          workerRef.current.postMessage({
            categories,
            searchTerm: value,
          })
        }
      }
    ),
    [categories]
  )

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    handleSearch(e.target.value)
  }

  return (
    <div className="mb-2 relative">
      <SearchIcon className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-gray-600" />
      <Input
        placeholder="Tìm kiếm trong nhà hàng"
        className="pl-10 bg-gray-50"
        value={searchTerm}
        onChange={handleSearchInputChange}
      />
    </div>
  )
}
